import secondsToMilliseconds from 'date-fns/secondsToMilliseconds';
import { useMemo } from 'react';
import { Subject, Observable } from 'rxjs';
import { debounceTime, filter, map, skip } from 'rxjs/operators';
import { environment } from '../../config';
import { EditorEvent } from './useEditorMachine';
import { useSubscription } from '../hooks/useSubscription';
import { BusDispatch, BusEvent } from './EditorContext';
import { DocumentEvent } from './useDocumentState';

export const DELAYS =
  environment === 'development'
    ? {
        DEBOUNCE_TIME: secondsToMilliseconds(0.5),
        IDLE_TIME: secondsToMilliseconds(3),
        POLLING_INTERVAL: secondsToMilliseconds(5),
      }
    : {
        DEBOUNCE_TIME: secondsToMilliseconds(0.5),
        IDLE_TIME: secondsToMilliseconds(5),
        POLLING_INTERVAL: secondsToMilliseconds(25),
      };

const useInputStart = (documentUpdate$: Observable<undefined>, dispatch: BusDispatch) =>
  useSubscription(
    () => documentUpdate$.subscribe(() => dispatch({ type: 'Editor', event: { type: 'INPUT' } })),
    [documentUpdate$, dispatch]
  );

const useInputEnd = (input$: Observable<undefined>, dispatch: BusDispatch) =>
  useSubscription(
    () =>
      input$.pipe(debounceTime(DELAYS.DEBOUNCE_TIME)).subscribe(() => {
        dispatch({ type: 'Editor', event: { type: 'INPUT_END' } });
      }),
    [input$, dispatch]
  );

export const useInputHandling = (bus$: Subject<BusEvent>, dispatch: BusDispatch) => {
  const documentUpdated$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Document'),
        map((e) => (e as { type: 'Document'; event: DocumentEvent }).event),
        filter((e) => e.type === 'BODY_UPDATED' || e.type === 'TITLE_UPDATED'),
        map((_) => undefined),
        skip(1) /** Note(Zaymon): Skip the first emission from setEditable. */
      ),
    [bus$]
  );

  useInputStart(documentUpdated$, dispatch);

  const input$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Editor'),
        map((e) => (e as { type: 'Editor'; event: EditorEvent }).event),
        filter((e) => e.type === 'INPUT'),
        map((_) => undefined)
      ),
    [bus$]
  );

  useInputEnd(input$, dispatch);
};
